/*
  Месяцы начисления полосами - левая колонка страницы «Как сформировался долг»
  (вариант В, выбран владельцем 05.10.2026).

  Над списком переключатель «Основной долг / Пени» (решение владельца того же
  дня: пени показываются «по аналогии с основным долгом»). Полоса - сальдо
  месяца на конец либо пени по нему, на одной шкале для всех месяцев: видно,
  когда долг рос быстрее и какие месяцы дают пени больше всего. Ширина -
  единственное, что считается здесь: это геометрия, а не деньги.

  Месяц - кнопка: нажатие показывает справа его услуги и расчёт пеней. Подъёма
  при наведении у неё нет (`no-hover-lift`): это строка списка, и прыгающий под
  курсором список не читается.
*/

import { monthShort, money, plural } from '../api/format.js'
import { Switch } from './Common.jsx'
import PageBar from './PageBar.jsx'
import usePaging from './usePaging.js'

const MODES = [
  { value: 'debt', title: 'Основной долг' },
  { value: 'penalty', title: 'Пени' },
]

const HINTS = {
  debt: 'Полоса - сколько за месяц осталось не оплачено.',
  penalty: 'Полоса - пени, набежавшие на долг этого месяца.',
}

/** Строки списка в выбранном виде: месяц и сумма полосы. */
function rowsOf(formation, mode) {
  if (mode === 'penalty') {
    return formation.penalty.months.map((month) => ({ period: month.period, amount: month.amount }))
  }
  return formation.months.map((month) => ({ period: month.period, amount: month.closing }))
}

function widthOf(amount, largest) {
  const value = Number(amount)
  return value > 0 && largest > 0 ? (value / largest) * 100 : 0
}

export default function DebtMonths({ formation, mode, onMode, chosen, onChoose }) {
  const shown = mode === 'penalty' && formation.penalty ? 'penalty' : 'debt'
  const rows = rowsOf(formation, shown)
  // Шкала полос считается по ВСЕМ месяцам, а не по странице: на второй странице полоса
  // того же размера обязана быть той же длины (21.10.2026, общая панель страниц).
  const largest = Math.max(0, ...rows.map((row) => Number(row.amount)))
  const { rows: pageRows, bar } = usePaging(rows)
  const total = shown === 'penalty' ? formation.penalty.total : formation.total.closing

  return (
    <section className="card">
      <h2 className="section__title">По месяцам начисления</h2>
      {formation.penalty ? <Switch value={shown} onChange={onMode} options={MODES} /> : null}
      <p className="muted debt-months__hint">
        {HINTS[shown]} Нажмите на месяц - справа появятся услуги и расчёт пеней.
      </p>

      <div className="debt-months">
        {pageRows.map((row) => (
          <button
            key={row.period}
            type="button"
            className={`debt-months__row no-hover-lift${
              row.period === chosen ? ' debt-months__row--chosen' : ''
            }`}
            aria-pressed={row.period === chosen}
            onClick={() => onChoose(row.period)}
          >
            <span className="debt-months__month">{monthShort(row.period)}</span>
            <span className="debt-months__track" aria-hidden="true">
              <span
                className="debt-months__fill"
                style={{ width: `${widthOf(row.amount, largest)}%` }}
              />
            </span>
            <span className="debt-months__sum">{money(row.amount)}</span>
          </button>
        ))}
      </div>
      <PageBar {...bar} what="месяцев" />

      <div className="debt-months__total">
        <span>{shown === 'penalty' ? 'Итого пени' : 'Итого'}</span>
        <span className="debt-months__sum">{money(total)}</span>
      </div>
      {shown === 'debt' && formation.months_without_debt > 0 ? (
        <div className="muted">
          Ещё {plural(formation.months_without_debt, 'месяц', 'месяца', 'месяцев')} оплачены
          полностью и в список не вошли.
        </div>
      ) : null}
    </section>
  )
}
